import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadTradeNotes, addTradeNote, loadUnreadCounts, applyReadPatches } from './actions'
import { ActivityItem, TradeNoteReadPatch, UnreadCounts } from './types'

const baseUrl = `${config.backendUrl}/api/trade-notes`

// No `?source=` param: since the read-source router landed on trunk (#8254), the backend
// resolves the read source itself, and no duck sends one. The endpoint still accepts an
// explicit `?source=pqs` override for debugging.

/**
 * A master trade's notes alone (ADR_LOS-028 D2): the Activity Stream's Refresh button. Opening
 * the tab reads the whole tab at once (loadTradeActivity, LOS-7095); a Refresh only needs the
 * notes, so it keeps the narrow read — three scans filtered to the trade, routed by the read
 * flag — rather than re-running the tab's full statement (review on #8472). Notes are scoped to
 * a sub-trade; the backend aggregates a master trade's in one request because each note
 * carries its masterTradeId.
 */
export const loadTradeNotesByMasterTrade =
  (masterTradeId: string) => async (dispatch: Dispatch) => {
    if (!masterTradeId) {
      return { success: false as const, payload: 'No master trade id' }
    }
    try {
      const res = await client.get(
        `${baseUrl}/master-trade/${masterTradeId}`,
      )
      dispatch(loadTradeNotes((res.data as unknown as ActivityItem[]) ?? []))
      return { success: true as const, payload: res.data }
    } catch (e: any) {
      // A FAILED load must not touch the store: dispatching [] here would merge nothing (a
      // silent no-op leaving stale notes rendered) — the caller surfaces the failure instead.
      return {
        success: false as const,
        payload:
          e?.response?.data?.message ?? 'Failed to load the activity stream',
      }
    }
  }

export const loadTradeNotesBySubTrade =
  (subTradeId: string) => async (dispatch: Dispatch) => {
    if (!subTradeId) {
      return { success: false as const, payload: 'No sub-trade id' }
    }
    try {
      const res = await client.get(
        `${baseUrl}/sub-trade/${subTradeId}`,
      )
      dispatch(loadTradeNotes((res.data as unknown as ActivityItem[]) ?? []))
      return { success: true as const, payload: res.data }
    } catch (e: any) {
      return {
        success: false as const,
        payload:
          e?.response?.data?.message ?? 'Failed to load the activity stream',
      }
    }
  }

/**
 * Create a note. Only the sub-trade and the body are sent: the recipients and the master trade
 * are resolved server-side from the note anchor (falling back to the sub-trade contract), so
 * the client cannot widen a note's disclosure or file it under another trade (ADR_LOS-028 D5).
 *
 * There is no edit or delete thunk, and that is deliberate — Phase 1 notes are create-only
 * pending an open product decision.
 */
export const createTradeNote =
  (subTradeId: string, body: string) => async (dispatch: Dispatch) => {
    try {
      const res = await client.post(baseUrl, { subTradeId, body })
      const created = res.data as unknown as ActivityItem
      if (created) {
        dispatch(addTradeNote(created))
      }
      return { success: true, payload: created }
    } catch (e: any) {
      return {
        success: false,
        payload: e.response?.data?.message ?? 'Failed to add note',
      }
    }
  }

/**
 * Mark a note read (read-state design). First click wins ON THE LEDGER — a repeat or
 * concurrent click returns the existing receipt with the original reader preserved. The
 * response is the updated stream item; upserting it is what clears the New badge.
 */
export const markTradeNoteRead =
  (noteId: string) => async (dispatch: Dispatch) => {
    try {
      const res = await client.post(`${baseUrl}/mark-read`, { noteId })
      dispatch(addTradeNote(res.data as unknown as ActivityItem))
      // The Stream-column badges read the counts slice; a click must decrement them too.
      await loadTradeNoteUnreadCounts()(dispatch)
      return { success: true, payload: res.data }
    } catch (e: any) {
      return {
        success: false,
        payload: e?.response?.data?.message ?? 'Failed to mark note as actioned',
      }
    }
  }

/**
 * Undo "Mark as actioned" (LOS-6988). The response is the updated stream item — unactioned
 * again, carrying the reopen — so upserting it is what brings back the New badge and button.
 * The badge counts reload so the note counts as unactioned again everywhere.
 */
export const reopenTradeNote =
  (noteId: string) => async (dispatch: Dispatch) => {
    try {
      const res = await client.post(`${baseUrl}/reopen`, { noteId })
      dispatch(addTradeNote(res.data as unknown as ActivityItem))
      await loadTradeNoteUnreadCounts()(dispatch)
      return { success: true, payload: res.data }
    } catch (e: any) {
      return {
        success: false,
        payload: e?.response?.data?.message ?? 'Failed to undo the action',
      }
    }
  }

/**
 * "Mark all N as actioned" (LOS-6989): one request for the whole batch. The backend marks each
 * note with its own receipt and reports per-note failures rather than failing the call, so a
 * partial success still lands every receipt in the store, and the badge counts reload ONCE for
 * the batch. The response is receipt patches, not whole notes (LOS-7093): the store already
 * holds the notes whose ids were sent, so only their read fields are patched.
 */
export const markTradeNotesReadBulk =
  (masterTradeId: string, noteIds: string[]) => async (dispatch: Dispatch) => {
    try {
      const res = await client.post(`${baseUrl}/mark-read-bulk`, { masterTradeId, noteIds })
      const data = res.data as unknown as {
        marked?: TradeNoteReadPatch[]
        failed?: { noteId: string; message: string }[]
      }
      const marked = data?.marked ?? []
      const failed = data?.failed ?? []
      if (marked.length > 0) dispatch(applyReadPatches(marked))
      await loadTradeNoteUnreadCounts()(dispatch)
      return { success: true, payload: { marked, failed } }
    } catch (e: any) {
      return {
        success: false,
        payload: e?.response?.data?.message ?? 'Failed to mark notes as actioned',
      }
    }
  }

/** Unread counts for the Stream-column badges (read-state design). Manual-refresh like every
 * other read here (D8): loaded when a badge surface mounts and after a mark-read.
 *
 * The sequence counter is an out-of-order guard: rapid clicks fire one refresh per mark-read,
 * and a SLOW STALE response must not overwrite a fresher one (last request wins, not last
 * response). */
let unreadCountsSeq = 0
export const loadTradeNoteUnreadCounts = () => async (dispatch: Dispatch) => {
  const seq = ++unreadCountsSeq
  try {
    const res = await client.get(`${baseUrl}/unread-counts`)
    if (seq === unreadCountsSeq) {
      dispatch(loadUnreadCounts(res.data as unknown as UnreadCounts))
    }
    return { success: true as const }
  } catch (e: any) {
    // Same contract as the loads above: a FAILED refresh must not touch the store. The counts
    // are wholesale-replaced by the reducer, so dispatching empty maps here would blank every
    // badge that was still correct a moment ago. Last-known counts stand until a refresh
    // succeeds; badges are ambient UI, so no toast — the result is returned for any caller
    // that needs to surface it.
    return {
      success: false as const,
      payload: e?.response?.data?.message ?? 'Failed to load unread counts',
    }
  }
}
